import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ErrorPage } from "@/components/ErrorPage";

export const Route = createFileRoute("/_auth")({
	component: Auth,
	errorComponent: AuthErrorPage,
});

function Auth() {
	const { t } = useTranslation();

	// Preserve the originally requested URL so we can redirect back to it
	// after the user completes sign-in/sign-up (e.g. staff invite links).
	// Using window.location here is safe in our SPA environment and keeps
	// things simple while still working for any protected route under /_auth.
	const redirectTo =
		typeof window !== "undefined"
			? `${window.location.pathname}${window.location.search}${window.location.hash}`
			: "";

	return (
		<div>
			<SignedIn>
				<Outlet />
			</SignedIn>
			<SignedOut>
				<div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
					<div className="w-full max-w-md mx-auto">
						<div className="bg-base-100 border border-base-300 rounded-xl shadow-lg p-8 text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Lock className="w-8 h-8 text-primary" />
							</div>
							<h1 className="text-2xl font-bold mb-2">
								{t("common.signInRequired")}
							</h1>
							<p className="text-base-content/70 mb-6">
								{t("common.signInRequiredDescription")}
							</p>
							<div className="flex flex-col gap-3">
								<Link
									to="/sign-in"
									search={{ redirectTo }}
									className="btn btn-primary text-primary-content rounded-full px-6 transition-all duration-300 hover:shadow-lg hover:bg-primary-focus"
								>
									{t("common.signIn")}
								</Link>
							</div>
							<div className="flex flex-col gap-3 mt-6">
								<p className="text-base-content/70">
									{t("common.dontHaveAccount")}
								</p>
								<Link
									to="/sign-up"
									search={{ redirectTo }}
									className="btn btn-primary text-primary-content rounded-full px-6 transition-all duration-300 hover:shadow-lg hover:bg-primary-focus"
								>
									{t("common.signUp")}
								</Link>
							</div>
						</div>
					</div>
				</div>
			</SignedOut>
		</div>
	);
}

function AuthErrorPage({ error }: { error: Error }) {
	const handleHome = () => {
		window.location.href = "/app/select-organization";
	};

	const handleRetry = () => {
		window.location.reload();
	};

	return (
		<ErrorPage
			error={error}
			showBackButton={true}
			showHomeButton={true}
			showRetryButton={true}
			onHome={handleHome}
			onRetry={handleRetry}
		/>
	);
}
